/* eslint-disable react-hooks/exhaustive-deps */
import TabNav from "components/TabNav";
import { useCallback, useEffect, useState } from "react";

import ConfirmationModal from "components/ConfirmationModal";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import AddAppealCouncilLitigationDecisionModal2 from "pages/appealRequests/fragments/AddAppealCouncilLitigationDecisionModal2";
import { useParams } from "react-router-dom";
import {
  addSummon,
  editComplaint,
  editInvestigation,
  fetchAccusedSummonByComplaintId,
  fetchAppealByComplaintId,
  fetchAppealDateValidation,
  fetchComplaintById,
  fetchCouncilLitigationBySummonId,
  fetchCouncilOfficeVErdictByComplaintId,
  fetchInvestigationByComplaintId,
  fetchLitigationBySummonId,
  fetchSummonByInvestigationId,
} from "services/prosecutorDecipilineService";
import {
  AccusedSummon,
  Appeal,
  Complaints,
  CouncilLitigation,
  Investigation,
  Litigation,
  Summon,
} from "types";
import {
  APPEAL_FEEDBACK_TYPE,
  COMPLIANT_STATUS,
  FEEDBACK_TYPE,
  INVESTIGATION_STATUS,
  PROSECUTOR_DECISION_TYPE,
  SUMMON_STATUS,
} from "utils/constantValues";
import { isInvestigationAllowed } from "utils/IsInvestigationAllowed";
import { ROLES } from "utils/roles";
import ActivityLogs from "./fragments/ActivityLogs";
import AddAccusedSummonModal from "./fragments/AddAccusedSummonModal";
import AddAppealModal from "./fragments/AddAppealModal";
import AddChargeBackFeedbackModal from "./fragments/AddChargeBackFeedbackModal";
import AddChargeDocModal from "./fragments/AddChargeDocModal";
import AddCouncilLitigationDecisionModal from "./fragments/AddCouncilLitigationDecisionModal";
import AddDailyHearingModal from "./fragments/AddDailyHearingModal";
import AddLetterModal from "./fragments/AddLetterModal";
import AddLitigationDecisionModal from "./fragments/AddLitigationDecisionModal";
import AddSummonModal from "./fragments/AddSummonModal";
import AppealCard from "./fragments/AppealCard";
import AssignedProsecutorHistory from "./fragments/AssignedProsecutorHistory";
// import ChargeDocumentCard from "./fragments/ChargeDocumentCard";
import ComplaintDetailInfoCard from "./fragments/ComplaintDetailInfoCard";
import CouncilLitigationCard from "./fragments/CouncilLitigationCard";
import CouncilVerdictCard from "./fragments/CouncilVerdictCard";
import DecisionCard from "./fragments/DecisionCard";
import ListEvidenceCard from "./fragments/ListEvidenceCard";
import ListReporterCard from "./fragments/ListReporterCard";
import ListVictimCard from "./fragments/ListVictimCard";
import ListWitnessCard from "./fragments/ListWitnessCard";
import LitigationCard from "./fragments/LitigationCard";
import RejectAppealModal from "./fragments/RejectAppealModal";
import SummonCard from "./fragments/SummonCard";
import SummonConfirmationStatus from "./fragments/SummonConfirmationStatus";
import TerminateLitigationModal from "./fragments/TerminateLitigationModal";
import ChargeDocumentCard from "./fragments/ChargeDocumentCard";
import { useAuth } from "contexts/AuthContext";

const calculate_remaining_days = ({ date }: { date: string }) => {
  const appeal = new Date(date ?? "");
  const today = new Date();

  // Zero out time to compare dates only
  appeal.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffInMs = appeal.getTime() - today.getTime();

  const remainingDays = Math.floor(diffInMs / msPerDay);

  return remainingDays;
};

export default function SingleComplaint() {
  const { t } = useTranslation(); // <-- Added for localization
  const { uu_id } = useParams();
  const { showToast } = useToast();
  const { user } = useAuth();

  const canCreateChargeDoc = useHasRole([ROLES.INSPECTION_PROSECUTOR]);
  const canApproveLetterCreation = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
  ]);
  const canCreateLetter = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canCreateSummon = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
  ]);
  const canCreateAppealSummon = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canSendInvestigationToCouncil = useHasRole([
    ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canViewSummon = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
    ROLES.INSPECTION_PROSECUTOR,
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canViewActivityLogs = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);
  const canSendInvestigationToCouncilHead = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canCreateInspectionDailyHearing = useHasRole([
    ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canCreateCouncilDailyHearing = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canTerminateComplaint = useHasRole([ROLES.INSPECTION_PROSECUTOR]);
  // const canTerminateInspectionLitigation = useHasRole([
  //   ROLES.INSPECTION_PROSECUTOR,
  // ]);
  // const canTerminateCouncilLitigation = useHasRole([
  //   ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  //   ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
  // ]);
  const canCreateLitigationDecision = useHasRole([ROLES.INSPECTION_PROSECUTOR]);
  const canCreateCouncilLitigationDecision = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canViewCompliantDetail = useHasRole([
    ROLES.INSPECTION_PROSECUTOR,
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);
  const canViewSummonConfirmation = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canViewAssignedHistory = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);
  const canRequestAppeal = useHasRole([ROLES.INSPECTION_PROSECUTOR]);
  const canViewChargeDoc = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
    ROLES.INSPECTION_PROSECUTOR,
    ROLES.INSPECTION_REGISTRAR,
  ]);

  const [complaintData, setComplaintData] = useState<
    Complaints | Complaints[]
  >();
  const [investigationData, setInvestigationData] = useState<Investigation>();
  const [allInvestigationData, setAllInvestigationData] =
    useState<Investigation[]>();
  const [summonData, setSummonData] = useState<Summon>();
  const [litigationData, setLitigationData] = useState<Litigation[]>();
  const [councilLitigationData, setCouncilLitigationData] =
    useState<CouncilLitigation[]>();
  const [appealData, setAppealData] = useState<Appeal[]>();
  const [accusedSummonData, setAccusedSummonData] = useState<AccusedSummon>();
  const [councilVerdict, setCouncilVerdict] = useState<CouncilLitigation[]>();

  const [isChargeDocOpen, setIsChargeDocOpen] = useState(false);
  const [isSummonOpen, setIsSummonOpen] = useState(false);
  const [isApproveCreateLetterOpen, setIsApproveCreateLetterOpen] =
    useState(false);
  const [isLetterOpen, setIsLetterOpen] = useState(false);
  const [isDailyHearingOpen, setIsDailyHearingOpen] = useState(false);
  const [isAppealFormOpen, setIsAppealFormOpen] = useState(false);
  const [isTerminateOpen, setIsTerminateOpen] = useState(false);
  const [isLitigationDecisionOpen, setIsLitigationDecisionOpen] =
    useState(false);
  const [isCouncilLitigationDecisionOpen, setIsCouncilLitigationDecisionOpen] =
    useState(false);
  const [isSendToCouncilModal, setIsSendToCouncilModal] =
    useState<boolean>(false);
  const [isBackToProsecutorModal, setIsBackToProsecutorModal] =
    useState<boolean>(false);
  const [isAccusedSummonOpen, setIsAccusedSummonOpen] = useState(false);
  // const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isAppealRejectOpen, setIsAppealRejectOpen] = useState(false);
  const [isInitialHearingDecisionOpen, setIsInitialHearingDecisionOpen] =
    useState(false);
  const [
    isAppealInitialHearingDecisionOpen,
    setIsAppealInitialHearingDecisionOpen,
  ] = useState(false);

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [appealDateValidation, setAppealDateValidation] = useState<number>(30);
  // const [loading, setLoading] = useState(false);
  // const [error, setError] = useState("");

  // Accordion state for sidebar sections
  type SectionKey = "evidence" | "reporter" | "victim" | "witness";
  const [accordionOpen, setAccordionOpen] = useState<
    Record<SectionKey, boolean>
  >({
    evidence: false,
    reporter: false,
    victim: false,
    witness: false,
  });

  const toggleSection = (key: SectionKey) =>
    setAccordionOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  const tabs = [
    t("detailInformation"),
    ((councilLitigationData?.length ?? 0) > 0 ||
      (litigationData?.length ?? 0) > 0) &&
      t("litigation"),
    (appealData?.length ?? 0) > 0 && t("appeal"),
    (councilVerdict?.length ?? 0) > 0 && "Appeal Decision On Initials Day",
  ].filter((tab): tab is string => Boolean(tab));
  const [activeTab, setActiveTab] = useState<string>(tabs[0]);
  const isRegionalComplaint =
    !!complaintData &&
    !Array.isArray(complaintData) &&
    !!complaintData.is_regional;

  const isNotFoundError = (error: any) => {
    const status =
      error?.response?.status ?? error?.status ?? error?.request?.status;
    return status === 404;
  };

  useEffect(() => {
    if (tabs.length > 0 && !tabs.includes(activeTab)) {
      setActiveTab(tabs[0]);
    }
  }, [tabs, activeTab]);

  const appealWarningMessage = (() => {
    const decisionDate = councilLitigationData?.[0]?.council_decision_day;

    if (!decisionDate) return null;

    const days =
      calculate_remaining_days({ date: decisionDate }) + appealDateValidation;
    const absDays = Math.abs(days);
    const dayLabel = absDays === 1 ? t("day") : t("days");

    if (days < 0) {
      return `${t("appealDeadlineStatus")}: ${t("expired")} ${absDays} ${dayLabel} ${t("ago")}`;
    }

    if (days === 0) {
      return `${t("appealDeadlineStatus")}: ${t("dueToday")}`;
    }

    if (days === 1) {
      return `${t("appealDeadlineStatus")}: ${t("dueTomorrow")}`;
    }

    return `${t("appealDeadlineStatus")}: ${days} ${dayLabel} ${t("remaining")}`;
  })();

  const loadInitialPageData = async () => {
    if (!uu_id) {
      setIsInitialLoading(false);
      return;
    }

    setIsInitialLoading(true);

    try {
      fetchAppealDateValidation().then((res) => {
        if (res && res.length > 0) {
          setAppealDateValidation(res[0].name ? parseInt(res[0].name) : 30);
        }
      });

      const complaintRes = await fetchComplaintById(uu_id);
      const latestComplaint = Array.isArray(complaintRes)
        ? complaintRes?.slice()?.reverse()[0]
        : complaintRes;

      setComplaintData(latestComplaint);

      let latestInvestigation: Investigation | undefined;
      try {
        const investigationRes = await fetchInvestigationByComplaintId(uu_id);
        if (investigationRes) {
          const sortedInvestigations = investigationRes?.slice()?.reverse();
          latestInvestigation = Array.isArray(investigationRes)
            ? sortedInvestigations[0]
            : investigationRes;
          setAllInvestigationData(sortedInvestigations);
          setInvestigationData(latestInvestigation);
        } else {
          setAllInvestigationData(undefined);
          setInvestigationData(undefined);
        }
      } catch (err) {
        if (isNotFoundError(err)) {
          setAllInvestigationData(undefined);
          setInvestigationData(undefined);
        } else {
          throw err;
        }
      }

      let latestSummon: Summon | undefined;
      if (latestInvestigation?.uu_id) {
        try {
          const summonRes = await fetchSummonByInvestigationId(
            latestInvestigation.uu_id,
          );
          latestSummon = Array.isArray(summonRes)
            ? summonRes?.slice()?.reverse()[0]
            : summonRes;
          setSummonData(latestSummon);
        } catch (err) {
          if (isNotFoundError(err)) {
            setSummonData(undefined);
          } else {
            throw err;
          }
        }
      } else {
        setSummonData(undefined);
      }

      if (latestSummon && latestComplaint) {
        try {
          const litigationRes = await fetchLitigationBySummonId(uu_id);
          setLitigationData(
            litigationRes?.sort(
              (a, b) =>
                new Date(b?.createdAt ?? "").getTime() -
                new Date(a?.createdAt ?? "").getTime(),
            ),
          );
        } catch (err) {
          if (isNotFoundError(err)) {
            setLitigationData([]);
          } else {
            throw err;
          }
        }
      } else {
        setLitigationData(undefined);
      }

      if (latestSummon?.id) {
        try {
          const councilLitigationRes = await fetchCouncilLitigationBySummonId(
            latestSummon.id,
          );
          setCouncilLitigationData(
            councilLitigationRes?.sort(
              (a, b) =>
                new Date(b?.createdAt ?? "").getTime() -
                new Date(a?.createdAt ?? "").getTime(),
            ),
          );
        } catch (err) {
          if (isNotFoundError(err)) {
            setCouncilLitigationData([]);
          } else {
            throw err;
          }
        }
      } else {
        setCouncilLitigationData(undefined);
      }

      const [appealRes, accusedSummonRes, councilVerdictRes] =
        await Promise.all([
          fetchAppealByComplaintId(uu_id).catch((err) => {
            if (isNotFoundError(err)) return [];
            throw err;
          }),
          fetchAccusedSummonByComplaintId(uu_id).catch((err) => {
            if (isNotFoundError(err)) return [];
            throw err;
          }),
          fetchCouncilOfficeVErdictByComplaintId(uu_id).catch((err) => {
            if (isNotFoundError(err)) return [];
            throw err;
          }),
        ]);

      setAppealData(appealRes ? appealRes?.slice()?.reverse() : []);

      setAccusedSummonData(
        accusedSummonRes
          ?.slice()
          ?.reverse()
          ?.filter(
            (obj) => obj.discipline_complain?.toString() === uu_id?.toString(),
          )[0],
      );

      setCouncilVerdict(
        councilVerdictRes ? councilVerdictRes?.slice()?.reverse() : [],
      );
    } catch (err) {
      showToast(t("failedToLoadComplaint"), "error");
    } finally {
      setIsInitialLoading(false);
    }
  };

  const loadComplaints = useCallback(async () => {
    if (!uu_id) return;
    // setLoading(true);
    try {
      const resData = await fetchComplaintById(uu_id);
      setComplaintData(
        Array.isArray(resData) ? resData?.slice()?.reverse()[0] : resData,
      );
      // setError("");
    } catch (err) {
      // setError(t("failedToLoadComplaint"));
    } finally {
      // setLoading(false);
    }
  }, [uu_id, t]);

  const loadInvestigations = useCallback(async () => {
    if (!complaintData || !uu_id) return;
    // setLoading(true);
    try {
      const resData = await fetchInvestigationByComplaintId(uu_id);
      if (resData) {
        setAllInvestigationData(resData?.slice()?.reverse());
        setInvestigationData(
          Array.isArray(resData) ? resData?.slice()?.reverse()[0] : resData,
        );
        // setError("");
      }
    } catch (err) {
      // setError(t("failedToLoadInvestigation"));
    } finally {
      // setLoading(false);
    }
  }, [complaintData, t]);

  const loadSummon = useCallback(async () => {
    if (!investigationData) return;
    // setLoading(true);
    try {
      const resData = await fetchSummonByInvestigationId(
        investigationData?.uu_id || "",
      );
      if (resData)
        setSummonData(
          Array.isArray(resData) ? resData?.slice()?.reverse()[0] : resData,
        );
      // setError("");
    } catch (err) {
      // setError(t("failedToLoadSummon"));
    } finally {
      // setLoading(false);
    }
  }, [investigationData, t]);

  const loadLitigation = useCallback(async () => {
    if (!complaintData || !summonData) return;
    // setLoading(true);
    try {
      const resData = await fetchLitigationBySummonId(uu_id || "");
      if (resData)
        setLitigationData(
          resData?.sort(
            (a, b) =>
              new Date(b?.createdAt ?? "").getTime() -
              new Date(a?.createdAt ?? "").getTime(),
          ),
        );
      // setError("");
    } catch (err) {
      // setError(t("failedToLoadLitigation"));
    } finally {
      // setLoading(false);
    }
  }, [complaintData, summonData, t]);

  const loadCouncilLitigation = useCallback(async () => {
    if (!summonData) return;
    // setLoading(true);
    try {
      const resData = await fetchCouncilLitigationBySummonId(
        summonData?.id || "",
      );
      if (resData)
        setCouncilLitigationData(
          resData?.sort(
            (a, b) =>
              new Date(b?.createdAt ?? "").getTime() -
              new Date(a?.createdAt ?? "").getTime(),
          ),
        );
      // setError("");
    } catch (err) {
      // setError(t("failedToLoadCouncilLitigation"));
    } finally {
      // setLoading(false);
    }
  }, [summonData, t]);

  const loadAppeal = useCallback(async () => {
    if (!complaintData || !uu_id) return;
    // setLoading(true);
    try {
      const resData = await fetchAppealByComplaintId(uu_id);
      if (resData) {
        setAppealData(resData?.slice()?.reverse());
        // setError("");
      }
    } catch (err) {
      // setError(t("failedToLoadAppeal"));
    } finally {
      // setLoading(false);
    }
  }, [complaintData, t]);

  const loadAccusedSummon = useCallback(async () => {
    if (!complaintData || !uu_id) return;
    // setLoading(true);
    try {
      const resData = await fetchAccusedSummonByComplaintId(uu_id);
      if (resData) {
        setAccusedSummonData(
          resData
            ?.slice()
            ?.reverse()
            ?.filter(
              (obj) =>
                obj.discipline_complain?.toString() === uu_id?.toString(),
            )[0],
        );
        // setError("");
      }
    } catch (err) {
      // setError(t("failedToLoadAccusedSummon"));
    } finally {
      // setLoading(false);
    }
  }, [complaintData, t]);

  const loadCoucilVerdict = useCallback(async () => {
    if (!complaintData || !uu_id) return;
    // setLoading(true);
    try {
      const resData = await fetchCouncilOfficeVErdictByComplaintId(uu_id);
      if (resData) {
        setCouncilVerdict(resData?.slice()?.reverse());
        // setError("");
      }
    } catch (err) {
      // setError(t("failedToLoadAppeal"));
    } finally {
      // setLoading(false);
    }
  }, [complaintData, t]);

  useEffect(() => {
    loadInitialPageData();
  }, [uu_id]);

  const handleSendToCouncilConfirm = async () => {
    if (!isSendToCouncilModal) return;

    if (!investigationData?.uu_id) {
      showToast(t("investigationNotFound"), "error");
      return;
    }

    setIsSubmitLoading(true);

    const data = new FormData();
    if (complaintData && !Array.isArray(complaintData)) {
      data.append(
        "assigned_prosecutor",
        complaintData?.user_id_accused_prosecutor_name || "",
      );
      data.append(
        "user_id_assigned_prosecutor",
        complaintData?.user_id_accused_prosecutor_name || "",
      );
      data.append(
        "accused_prosecutor_name",
        complaintData?.accused_prosecutor_name || "",
      );
      data.append(
        "user_id_accused_prosecutor_name",
        complaintData?.user_id_accused_prosecutor_name || "",
      );
      data.append(
        "accused_prosecutor_org_id",
        complaintData?.accused_prosecutor_org_id || "",
      );
      data.append(
        "accused_prosecutor_admin_unit_id",
        complaintData?.accused_prosecutor_admin_unit_id || "",
      );
      data.append("admin_unit_id", complaintData?.admin_unit_id || "");
      data.append("org_id", complaintData?.org_id || "");
      data.append("complaint", String(complaintData.uu_id));
    }

    if (
      investigationData?.status === INVESTIGATION_STATUS.ON_CHARGE &&
      !investigationData?.registration_number
    ) {
      data.append("status", INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR);
    } else if (
      investigationData?.status === INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR
    ) {
      data.append("status", INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL);
    } else if (
      investigationData?.status === INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL
    ) {
      data.append("status", INVESTIGATION_STATUS.FORWARDED_TO_HEAD);
    }

    const investigation_id = investigationData?.uu_id;
    const isFormData = true;

    try {
      const resData = await editInvestigation(
        data,
        investigation_id,
        isFormData,
      );

      if (
        resData &&
        investigationData?.status ===
          INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR
      ) {
        await editComplaint({
          ...(complaintData as Complaints),
          has_councile: true,
          status: COMPLIANT_STATUS.COUNCIL_OFFICE,
        });
      }

      loadInvestigations();
      setIsSendToCouncilModal(false);
      showToast(t("sent"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleApproveLetterCreationSubmit = async () => {
    setIsSubmitLoading(true);

    if (!investigationData?.uu_id) {
      showToast(t("investigationNotFound"), "error");
      return;
    }

    const data = new FormData();
    data.append("status", SUMMON_STATUS.FORWARDED_TO_COUNCIL_OFFICE);
    data.append("investigation", investigationData?.uu_id);
    if (complaintData && !Array.isArray(complaintData)) {
      data.append("admin_unit_id", complaintData.admin_unit_id || "");
      data.append("org_id", complaintData.org_id || "");
      data.append(
        "accused_prosecutor_name",
        complaintData?.accused_prosecutor_name || "",
      );
      data.append(
        "user_id_accused_prosecutor_name",
        complaintData?.user_id_accused_prosecutor_name || "",
      );
      data.append(
        "accused_prosecutor_org_id",
        complaintData?.accused_prosecutor_org_id || "",
      );
      data.append(
        "accused_prosecutor_admin_unit_id",
        complaintData?.accused_prosecutor_admin_unit_id || "",
      );
      data.append(
        "assigned_prosecutor",
        complaintData?.user_id_accused_prosecutor_name || "",
      );
      data.append(
        "user_id_assigned_prosecutor",
        complaintData?.user_id_accused_prosecutor_name || "",
      );
    }

    const isFormData = true;

    try {
      await addSummon(data, isFormData);

      loadSummon();
      setIsApproveCreateLetterOpen(false);
      showToast(t("successfullyCreated"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-semibold text-gray-700 flex items-center gap-2">
          <span>💼</span>
          {t("complaintDetail")}
        </h1>
        {/* Reload Button */}
        <button
          className="ml-4 px-4 py-2 flex items-center gap-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 border border-gray-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          type="button"
          title={t("reload")}
          aria-label={t("reload")}
          onClick={loadInitialPageData}
          disabled={isInitialLoading}
        >
          {isInitialLoading ? (
            <svg
              className="animate-spin h-5 w-5 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          ) : (
            <span className="text-lg">&#x21bb;</span>
          )}
          <span>{isInitialLoading ? t("loading") : t("reload")}</span>
        </button>
      </div>

      {/* Header: Tabs */}
      <div className="w-full flex flex-wrap items-center justify-between gap-4 mb-5">
        <TabNav active={activeTab} tabs={tabs} onSelect={setActiveTab} />

        <div className="text-right">
          {activeTab === tabs[0] && (
            <div className="flex gap-4">
              {canCreateChargeDoc &&
                complaintData &&
                !Array.isArray(complaintData) &&
                isInvestigationAllowed(
                  complaintData?.offence_type,
                  complaintData?.assigned_date,
                ) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                investigationData?.status ===
                  INVESTIGATION_STATUS.UNDER_INVESTIGATION &&
                investigationData?.prosecutor_decision_type ===
                  PROSECUTOR_DECISION_TYPE.FRAME_CHARGE &&
                investigationData?.feedback_status === FEEDBACK_TYPE.APPROVED &&
                investigationData?.feedback_status_charge_docu !==
                  FEEDBACK_TYPE.APPROVED && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsChargeDocOpen(true)}
                  >
                    {t("uploadChargeDocument")}
                  </button>
                )}

              {((canSendInvestigationToCouncil &&
                complaintData &&
                !Array.isArray(complaintData) &&
                isInvestigationAllowed(
                  complaintData?.offence_type,
                  complaintData?.assigned_date,
                ) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                ((investigationData?.status ===
                  INVESTIGATION_STATUS.ON_CHARGE &&
                  investigationData?.feedback_status_charge_docu ===
                    FEEDBACK_TYPE.APPROVED &&
                  !investigationData?.registration_number) ||
                  (investigationData?.status ===
                    INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR &&
                    investigationData?.registration_number))) ||
                (canSendInvestigationToCouncilHead &&
                  investigationData?.status ===
                    INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL)) &&
                !summonData &&
                user?.admin_unit ===
                  investigationData?.complaint_detail?.admin_unit_id &&
                investigationData?.registration_number && (
                  <button
                    className="bg-green-600 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsSendToCouncilModal(true)}
                  >
                    {investigationData?.status ===
                    INVESTIGATION_STATUS.ON_CHARGE
                      ? t("sendToRegistrar")
                      : investigationData?.status ===
                          INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR
                        ? t("sendToCouncilOffice")
                        : isRegionalComplaint
                          ? t("sendToRegionalCouncilHead")
                          : t("sendToZonalCouncilHead")}
                  </button>
                )}

              {canSendInvestigationToCouncilHead &&
                complaintData &&
                !Array.isArray(complaintData) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                investigationData?.status ===
                  INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL && (
                  <button
                    className="bg-red-600 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsBackToProsecutorModal(true)}
                  >
                    {t("backToProsecutor")}
                  </button>
                )}

              {canApproveLetterCreation &&
                complaintData &&
                !Array.isArray(complaintData) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                investigationData?.status ===
                  INVESTIGATION_STATUS.FORWARDED_TO_HEAD &&
                !summonData && (
                  <button
                    className="bg-green-600 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsApproveCreateLetterOpen(true)}
                  >
                    {t("approvedToCreateLetter")}
                  </button>
                )}

              {canApproveLetterCreation &&
                complaintData &&
                !Array.isArray(complaintData) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                investigationData?.status ===
                  INVESTIGATION_STATUS.FORWARDED_TO_HEAD &&
                !summonData && (
                  <button
                    className="bg-red-600 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsBackToProsecutorModal(true)}
                  >
                    {t("backToCouncilOfficer")}
                  </button>
                )}

              {canCreateLetter &&
                complaintData &&
                !Array.isArray(complaintData) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                summonData &&
                summonData?.status ===
                  SUMMON_STATUS.FORWARDED_TO_COUNCIL_OFFICE && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsLetterOpen(true)}
                  >
                    {t("createSummon")}
                  </button>
                )}

              {canCreateSummon &&
                complaintData &&
                !Array.isArray(complaintData) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                summonData &&
                summonData?.accused_response_details &&
                // summonData?.accused_prosecutor_name &&
                !summonData?.hearing_date && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsSummonOpen(true)}
                  >
                    {t("createASummon")}
                  </button>
                )}

              {canTerminateComplaint &&
                complaintData &&
                !Array.isArray(complaintData) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED && (
                  <button
                    className="bg-red-900 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsTerminateOpen(true)}
                  >
                    {t("terminate")}
                  </button>
                )}
            </div>
          )}

          {activeTab === tabs[1] && (
            <div className="flex gap-4">
              {complaintData &&
                !Array.isArray(complaintData) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                // summonData?.accused_confirmed &&
                summonData?.cordinator_confirmed &&
                ((canCreateInspectionDailyHearing &&
                  (!litigationData ||
                    (litigationData &&
                      !litigationData[0]?.is_final_verdict_given))) ||
                  (canCreateCouncilDailyHearing &&
                    (!councilLitigationData ||
                      (councilLitigationData &&
                        councilLitigationData?.every(
                          (obj) => obj?.is_final_verdict_given === false,
                        ))))) && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsDailyHearingOpen(true)}
                  >
                    {canCreateCouncilDailyHearing &&
                    councilLitigationData?.length === 0
                      ? t("addInitialHearing")
                      : t("addDailyHearing")}
                  </button>
                )}

              {canCreateLitigationDecision &&
                complaintData &&
                !Array.isArray(complaintData) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                litigationData &&
                litigationData[0]?.is_final_verdict_given &&
                !litigationData[0]?.council_decision_summary && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsLitigationDecisionOpen(true)}
                  >
                    {t("addLitigationDecision")}
                  </button>
                )}

              {canCreateCouncilLitigationDecision &&
                complaintData &&
                !Array.isArray(complaintData) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                councilLitigationData &&
                councilLitigationData?.some(
                  (obj) => obj?.is_final_verdict_given,
                ) &&
                !councilLitigationData[0]?.council_decision_summary &&
                !councilLitigationData[0]?.council_decision && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsCouncilLitigationDecisionOpen(true)}
                  >
                    {t("addCouncilOfficeDecision")}
                  </button>
                )}
              {complaintData &&
                !Array.isArray(complaintData) &&
                (isRegionalComplaint
                  ? canCreateSummon
                  : canCreateAppealSummon) &&
                !Array.isArray(complaintData) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                !accusedSummonData &&
                councilLitigationData &&
                councilLitigationData?.some(
                  (obj) => obj?.is_final_verdict_given,
                ) && (
                  <button
                    className="bg-green-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsAccusedSummonOpen(true)}
                  >
                    {t("createAccusedAppeal")}
                  </button>
                )}
              {/* 
              {canCreateAppealSummon &&
                complaintData &&
                !Array.isArray(complaintData) &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                !accusedSummonData &&
                councilLitigationData &&
                councilLitigationData?.some(
                  (obj) => obj?.is_final_verdict_given
                ) &&
                (councilLitigationData[0]?.council_decision_summary ||
                  councilLitigationData[0]?.council_decision) &&
                councilLitigationData[0]?.claim_summary && (
                  <button
                    className="bg-green-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsAccusedSummonOpen(true)}
                  >
                    {t("createAccusedAppeal")}
                  </button>
                )}
              {canCreateAppealSummon &&
                complaintData &&
                !Array.isArray(complaintData) &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                !accusedSummonData &&
                councilLitigationData &&
                councilLitigationData?.some(
                  (obj) => obj?.is_final_verdict_given
                ) &&
                (councilLitigationData[0]?.council_decision_summary ||
                  councilLitigationData[0]?.council_decision) &&
                councilLitigationData[0]?.claim_summary && (
                  <button
                    className="bg-green-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsInitialHearingDecisionOpen(true)}
                  >
                    Add Inintial Hearing Decision
                  </button>
                )} */}

              {canRequestAppeal &&
                complaintData &&
                !Array.isArray(complaintData) &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                litigationData &&
                litigationData[0]?.is_final_verdict_given &&
                !litigationData[0]?.termination_reason &&
                litigationData[0]?.council_decision_summary &&
                // litigationData[0]?.prosecutor_opinion_on_decision_appeal ===
                //   PROSECUTOR_APPEAL_OPINION.APPEAL &&
                councilLitigationData &&
                calculate_remaining_days({
                  date: councilLitigationData[0]?.council_decision_day ?? "",
                }) > -appealDateValidation &&
                councilLitigationData &&
                councilLitigationData?.some(
                  (obj) => obj?.is_final_verdict_given,
                ) &&
                (councilLitigationData[0]?.council_decision_summary ||
                  councilLitigationData[0]?.council_decision) &&
                appealData &&
                (appealData?.length <= 0 ||
                  (appealData[0]?.feedback_on_memorandum &&
                    appealData[0]?.appeal_feedback_status ===
                      APPEAL_FEEDBACK_TYPE.RESPOND_BACK)) && (
                  <div className="flex flex-row gap-4">
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800">
                      {appealWarningMessage}
                    </div>

                    <button
                      className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                      type="submit"
                      onClick={() => setIsAppealFormOpen(true)}
                    >
                      {appealData &&
                      appealData[0]?.appeal_feedback_status ===
                        APPEAL_FEEDBACK_TYPE.RESPOND_BACK
                        ? t("updateAppeal")
                        : t("requestForAppeal")}
                    </button>
                  </div>
                )}
            </div>
          )}

          {activeTab === tabs[2] && (
            <div className="flex gap-4">
              {/* {canCreateAppealSummon &&
                complaintData &&
                !Array.isArray(complaintData) &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                !accusedSummonData &&
                councilLitigationData &&
                councilLitigationData?.some(
                  (obj) => obj?.is_final_verdict_given,
                ) &&
                (councilLitigationData[0]?.council_decision_summary ||
                  councilLitigationData[0]?.council_decision) && (
                  <button
                    className="bg-green-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsAppealInitialHearingDecisionOpen(true)}
                  >
                    Add Inintial Hearing Decision
                  </button>
                )} */}
              {/* {canCreateAppealSummon && (
                <button
                  className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                  type="submit"
                  onClick={() => setIsAppealRejectOpen(true)}
                >
                  Reject
                </button>
              )} */}
            </div>
          )}
        </div>
      </div>

      <div className="relative">
        <div
          className={`grid grid-cols-1 lg:grid-cols-3 gap-4 ${
            isInitialLoading ? "pointer-events-none opacity-40" : ""
          }`}
        >
          {/* Detail Info */}
          {activeTab === tabs[0] && (
            <div className="lg:col-span-2 space-y-4">
              {canViewSummon &&
                summonData &&
                investigationData &&
                complaintData &&
                !Array.isArray(complaintData) && (
                  <SummonCard
                    complaintData={complaintData}
                    summonData={summonData}
                    compliantData={complaintData}
                    investigationData={investigationData}
                    onSummonRefresh={loadSummon}
                    onComplaintsRefresh={loadComplaints}
                  />
                )}

              {investigationData &&
                complaintData &&
                !Array.isArray(complaintData) &&
                (canViewChargeDoc ||
                  investigationData?.status ===
                    INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL ||
                  investigationData?.status ===
                    INVESTIGATION_STATUS.FORWARDED_TO_HEAD ||
                  investigationData?.status ===
                    INVESTIGATION_STATUS.BACK_TO_PROSECUTOR) && (
                  <ChargeDocumentCard
                    investigationData={investigationData}
                    complaintData={complaintData}
                    onInvestigationRefresh={loadInvestigations}
                    onComplaintRefresh={loadComplaints}
                  />
                )}
              {canViewCompliantDetail &&
                complaintData &&
                !Array.isArray(complaintData) &&
                complaintData.status !== COMPLIANT_STATUS?.PENDING && (
                  // investigationData?.prosecutor_decision_type !==
                  //   PROSECUTOR_DECISION_TYPE?.FRAME_CHARGE &&
                  <DecisionCard
                    complaintData={complaintData}
                    investigationData={investigationData}
                    allInvestigationData={allInvestigationData}
                    onInvestigationRefresh={loadInvestigations}
                    onComplaintsRefresh={loadComplaints}
                  />
                )}
              {canViewCompliantDetail && (
                <ComplaintDetailInfoCard
                  complaintData={complaintData}
                  onComplaintsRefresh={loadComplaints}
                />
              )}
            </div>
          )}

          {/* Litigation */}
          {activeTab === tabs[1] && (
            <div className="lg:col-span-2 space-y-4">
              {complaintData &&
                !Array.isArray(complaintData) &&
                councilLitigationData &&
                councilLitigationData?.length > 0 && (
                  <CouncilLitigationCard
                    complaintData={complaintData}
                    accusedSummonData={accusedSummonData}
                    councilLitigationData={councilLitigationData}
                    onCouncilLitigationRefresh={loadCouncilLitigation}
                    onLitigationRefresh={loadLitigation}
                  />
                )}

              {litigationData && litigationData?.length > 0 && (
                <LitigationCard
                  litigationData={litigationData}
                  councilLitigationData={councilLitigationData || []}
                  onLitigationRefresh={loadLitigation}
                />
              )}
            </div>
          )}

          {/* Appeal */}
          {activeTab === tabs[2] && (
            <div className="lg:col-span-2 space-y-4">
              {complaintData &&
                !Array.isArray(complaintData) &&
                appealData &&
                appealData?.length > 0 && (
                  <AppealCard
                    complaintData={complaintData}
                    appealData={appealData?.reverse()?.slice(0, 1)}
                    councilLitigationData={councilLitigationData}
                    onAppealRefresh={loadAppeal}
                  />
                )}
            </div>
          )}
          {activeTab === "Appeal Decision On Initials Day" && (
            <div className="lg:col-span-2 space-y-4">
              {complaintData &&
                !Array.isArray(complaintData) &&
                councilVerdict &&
                councilVerdict?.length > 0 && (
                  <CouncilVerdictCard
                    complaintData={complaintData}
                    councilVerdict={councilVerdict}
                    onAppealRefresh={loadAppeal}
                  />
                )}
            </div>
          )}

          {/* Sidebar */}
          <div className="space-y-4">
            {canViewActivityLogs &&
              complaintData &&
              !Array.isArray(complaintData) && (
                <ActivityLogs complaintData={complaintData} />
              )}

            {activeTab === tabs[0] &&
              canViewSummonConfirmation &&
              summonData &&
              summonData?.hearing_date && (
                <SummonConfirmationStatus summonData={summonData} />
              )}

            {canViewAssignedHistory &&
              complaintData &&
              !Array.isArray(complaintData) && (
                <AssignedProsecutorHistory
                  data={[
                    {
                      assignedTo: complaintData?.assigned_prosecutor,
                      assignedDate: complaintData?.assigned_date,
                    },
                    {
                      assignedTo: litigationData?.[0]?.re_assign_prosecutor,
                      assignedDate: new Date(
                        litigationData?.[0]?.createdAt ?? "",
                      )?.toLocaleDateString(),
                    },
                  ]}
                />
              )}

            {canViewCompliantDetail && (
              <div className="space-y-2">
                {/* Evidence */}
                <div className="border rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleSection("evidence")}
                    className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-t-lg"
                  >
                    <span className="text-sm font-semibold text-gray-700">
                      Evidence
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-4 w-4 transition-transform ${
                        accordionOpen.evidence ? "rotate-180" : "rotate-0"
                      }`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {accordionOpen.evidence && (
                    <div className="p-3">
                      <ListEvidenceCard
                        complaintData={complaintData}
                        onComplaintsRefresh={loadComplaints}
                      />
                    </div>
                  )}
                </div>

                {/* Reporters */}
                <div className="border rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleSection("reporter")}
                    className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-t-lg"
                  >
                    <span className="text-sm font-semibold text-gray-700">
                      Reporters
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-4 w-4 transition-transform ${
                        accordionOpen.reporter ? "rotate-180" : "rotate-0"
                      }`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {accordionOpen.reporter && (
                    <div className="p-3">
                      <ListReporterCard
                        complaintData={complaintData}
                        onComplaintsRefresh={loadComplaints}
                      />
                    </div>
                  )}
                </div>

                {/* Victims */}
                <div className="border rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleSection("victim")}
                    className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-t-lg"
                  >
                    <span className="text-sm font-semibold text-gray-700">
                      Victims
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-4 w-4 transition-transform ${
                        accordionOpen.victim ? "rotate-180" : "rotate-0"
                      }`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {accordionOpen.victim && (
                    <div className="p-3">
                      <ListVictimCard
                        complaintData={complaintData}
                        onComplaintsRefresh={loadComplaints}
                      />
                    </div>
                  )}
                </div>

                {/* Witnesses */}
                <div className="border rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleSection("witness")}
                    className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-t-lg"
                  >
                    <span className="text-sm font-semibold text-gray-700">
                      Witnesses
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-4 w-4 transition-transform ${
                        accordionOpen.witness ? "rotate-180" : "rotate-0"
                      }`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {accordionOpen.witness && (
                    <div className="p-3">
                      <ListWitnessCard
                        complaintData={complaintData}
                        onComplaintsRefresh={loadComplaints}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {isInitialLoading && (
          <div className="absolute inset-0 z-20 flex justify-center items-start pt-8 rounded-xl bg-gradient-to-br from-white/90 to-red-50/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3 rounded-lg border border-red-100 bg-white/80 px-6 py-5 shadow-sm">
              <div className="h-10 w-10 border-4 border-red-600/80 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-semibold text-gray-700">Loading...</p>
            </div>
          </div>
        )}
      </div>

      {/* /////////////////// MODAL FORMS ///////////////////  */}
      {complaintData && !Array.isArray(complaintData) && investigationData && (
        <AddChargeDocModal
          isOpen={isChargeDocOpen}
          setIsOpen={setIsChargeDocOpen}
          complaintData={complaintData}
          investigationData={investigationData}
          onInvestigationRefresh={loadInvestigations}
        />
      )}

      {complaintData && !Array.isArray(complaintData) && investigationData && (
        <AddChargeBackFeedbackModal
          isOpen={isBackToProsecutorModal}
          setIsOpen={setIsBackToProsecutorModal}
          complaintData={complaintData}
          investigationData={investigationData}
          onRefresh={loadInvestigations}
        />
      )}

      {investigationData &&
        summonData &&
        complaintData &&
        !Array.isArray(complaintData) && (
          <AddLetterModal
            isOpen={isLetterOpen}
            setIsOpen={setIsLetterOpen}
            summonData={summonData}
            complaintData={complaintData}
            investigationData={investigationData}
            onSummonRefresh={loadInitialPageData}
          />
        )}
      {investigationData &&
        summonData &&
        complaintData &&
        !Array.isArray(complaintData) && (
          <AddSummonModal
            isOpen={isSummonOpen}
            setIsOpen={setIsSummonOpen}
            summonData={summonData}
            complaintData={complaintData}
            investigationData={investigationData}
            onSummonRefresh={loadInvestigations}
          />
        )}

      {complaintData &&
        !Array.isArray(complaintData) &&
        summonData &&
        !Array.isArray(summonData) && (
          <AddDailyHearingModal
            isOpen={isDailyHearingOpen}
            setIsOpen={setIsDailyHearingOpen}
            summonData={summonData}
            complaintData={complaintData}
            onLitigationRefresh={loadInitialPageData}
          />
        )}

      {complaintData && !Array.isArray(complaintData) && (
        <AddAppealModal
          isOpen={isAppealFormOpen}
          setIsOpen={setIsAppealFormOpen}
          complaintData={complaintData}
          onCompliantRefresh={loadInitialPageData}
        />
      )}

      {litigationData && complaintData && !Array.isArray(complaintData) && (
        <AddLitigationDecisionModal
          isOpen={isLitigationDecisionOpen}
          setIsOpen={setIsLitigationDecisionOpen}
          litigationData={litigationData[0]}
          complaintData={complaintData}
          onRefresh={loadLitigation}
        />
      )}

      {councilLitigationData &&
        complaintData &&
        !Array.isArray(complaintData) && (
          <AddCouncilLitigationDecisionModal
            isOpen={isCouncilLitigationDecisionOpen}
            setIsOpen={setIsCouncilLitigationDecisionOpen}
            councilLitigationData={councilLitigationData[0]}
            complaintData={complaintData}
            onRefresh={loadCouncilLitigation}
          />
        )}

      {complaintData && !Array.isArray(complaintData) && (
        <TerminateLitigationModal
          isOpen={isTerminateOpen}
          setIsOpen={setIsTerminateOpen}
          complaintData={complaintData}
          onRefresh={loadComplaints}
        />
      )}

      {councilLitigationData &&
        complaintData &&
        !Array.isArray(complaintData) && (
          <AddAccusedSummonModal
            isOpen={isAccusedSummonOpen}
            setIsOpen={setIsAccusedSummonOpen}
            complaintData={complaintData}
            onComplaintRefresh={loadComplaints}
          />
        )}

      {complaintData && appealData && Array.isArray(appealData) && (
        <RejectAppealModal
          isOpen={isAppealRejectOpen}
          setIsOpen={setIsAppealRejectOpen}
          complaintData={complaintData as Complaints}
          appealData={appealData[0]}
          onComplaintRefresh={loadLitigation}
        />
      )}

      {complaintData &&
        !Array.isArray(complaintData) &&
        councilLitigationData &&
        Array.isArray(councilLitigationData) && (
          <AddAppealCouncilLitigationDecisionModal2
            isOpen={isInitialHearingDecisionOpen}
            setIsOpen={setIsInitialHearingDecisionOpen}
            complaintData={complaintData}
            councilLitigationData={councilLitigationData[0]}
            onRefresh={loadLitigation}
          />
        )}

      {complaintData &&
        !Array.isArray(complaintData) &&
        appealData &&
        councilLitigationData &&
        Array.isArray(councilLitigationData) && (
          <AddAppealCouncilLitigationDecisionModal2
            isOpen={isAppealInitialHearingDecisionOpen}
            setIsOpen={setIsAppealInitialHearingDecisionOpen}
            complaintData={complaintData}
            // appealData={appealData[0]}
            onRefresh={loadLitigation}
            councilLitigationData={councilLitigationData[0]}
          />
        )}

      <ConfirmationModal
        isOpen={isApproveCreateLetterOpen}
        onCancel={() => setIsApproveCreateLetterOpen(false)}
        onConfirm={handleApproveLetterCreationSubmit}
        isLoading={isSubmitLoading}
      />

      <ConfirmationModal
        isOpen={isSendToCouncilModal}
        onCancel={() => setIsSendToCouncilModal(false)}
        onConfirm={handleSendToCouncilConfirm}
        isLoading={isSubmitLoading}
      />
    </div>
  );
}
